import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type CopyButtonVariant = 'default' | 'ghost' | 'outline';
export type CopyButtonSize = 'sm' | 'md' | 'lg';
export type CopyFeedback = 'icon' | 'text' | 'tooltip' | 'none';

export interface CopyButtonProps {
  /** Text to copy to clipboard */
  text: string;
  /** Button variant */
  variant?: CopyButtonVariant;
  /** Button size */
  size?: CopyButtonSize;
  /** Feedback style when copied */
  feedback?: CopyFeedback;
  /** Custom label for copy action */
  label?: string;
  /** Custom label for copied state */
  copiedLabel?: string;
  /** Duration to show copied state (ms) */
  copiedDuration?: number;
  /** Callback when copy succeeds */
  onCopy?: (text: string) => void;
  /** Callback when copy fails */
  onError?: (error: Error) => void;
  /** Show text label */
  showLabel?: boolean;
  /** Disable button */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  /** ARIA label */
  'aria-label'?: string;
}

export interface CopyToClipboardProps {
  /** Text to copy */
  text: string;
  /** Render function receiving copy handler and state */
  children: (props: {
    copy: () => void;
    copied: boolean;
    error: Error | null;
  }) => React.ReactNode;
  /** Duration to show copied state (ms) */
  copiedDuration?: number;
  /** Callback when copy succeeds */
  onCopy?: (text: string) => void;
  /** Callback when copy fails */
  onError?: (error: Error) => void;
}

export interface ClipboardTextProps {
  /** Text to display and copy */
  text: string;
  /** Maximum characters to show before truncating */
  maxLength?: number;
  /** Show full text in tooltip on hover */
  showTooltip?: boolean;
  /** Make text selectable */
  selectable?: boolean;
  /** Hide copy button until hover */
  hideButtonUntilHover?: boolean;
  /** Copy button size */
  size?: CopyButtonSize;
  /** Additional class names */
  className?: string;
}

export interface CodeBlockProps {
  /** Code content */
  code: string;
  /** Programming language for syntax reference (visual only) */
  language?: string;
  /** Show line numbers */
  showLineNumbers?: boolean;
  /** Highlight specific lines (1-indexed) */
  highlightLines?: number[];
  /** Show copy button */
  showCopyButton?: boolean;
  /** Maximum height before scrolling */
  maxHeight?: number | string;
  /** Additional class names */
  className?: string;
  /** Title/filename for the code block */
  title?: string;
}

export interface CopyInputProps {
  /** Value to display and copy */
  value: string;
  /** Label for the input */
  label?: string;
  /** Whether the input is readonly */
  readOnly?: boolean;
  /** Input size */
  size?: 'sm' | 'md' | 'lg';
  /** Show copy button */
  showCopyButton?: boolean;
  /** Callback when copied */
  onCopy?: (value: string) => void;
  /** Additional class names */
  className?: string;
}

export interface CopyLinkProps {
  /** URL to display and copy */
  url: string;
  /** Display text (defaults to URL) */
  displayText?: string;
  /** Truncate URL display */
  truncate?: boolean;
  /** Maximum characters before truncating */
  maxLength?: number;
  /** Show as actual link */
  asLink?: boolean;
  /** Callback when copied */
  onCopy?: (url: string) => void;
  /** Additional class names */
  className?: string;
}

export interface UseCopyToClipboardOptions {
  /** Duration to show copied state (ms) */
  copiedDuration?: number;
  /** Callback when copy succeeds */
  onCopy?: (text: string) => void;
  /** Callback when copy fails */
  onError?: (error: Error) => void;
}

export interface UseCopyToClipboardReturn {
  /** Whether text was recently copied */
  copied: boolean;
  /** Error if copy failed */
  error: Error | null;
  /** Copy text to clipboard */
  copy: (text: string) => Promise<void>;
  /** Reset copied state */
  reset: () => void;
}

// ============================================================================
// Hook: useCopyToClipboard
// ============================================================================

export function useCopyToClipboard(
  options: UseCopyToClipboardOptions = {}
): UseCopyToClipboardReturn {
  const { copiedDuration = 2000, onCopy, onError } = options;

  const [copied, setCopied] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  const reset = React.useCallback(() => {
    setCopied(false);
    setError(null);
  }, []);

  const copy = React.useCallback(async (text: string) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    try {
      // Use Clipboard API if available
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);

        if (!successful) {
          throw new Error('Copy command failed');
        }
      }

      setCopied(true);
      setError(null);
      onCopy?.(text);

      // Reset after duration
      timeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, copiedDuration);
    } catch (err) {
      const copyError = err instanceof Error ? err : new Error('Copy failed');
      setError(copyError);
      setCopied(false);
      onError?.(copyError);
    }
  }, [copiedDuration, onCopy, onError]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { copied, error, copy, reset };
}

// ============================================================================
// Style Utilities
// ============================================================================

const sizeStyles: Record<CopyButtonSize, { button: string; icon: string }> = {
  sm: { button: 'h-7 px-2 text-xs', icon: 'w-3.5 h-3.5' },
  md: { button: 'h-8 px-3 text-sm', icon: 'w-4 h-4' },
  lg: { button: 'h-10 px-4 text-base', icon: 'w-5 h-5' },
};

const iconOnlySizes: Record<CopyButtonSize, string> = {
  sm: 'h-7 w-7',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
};

function getVariantStyles(variant: CopyButtonVariant): string {
  switch (variant) {
    case 'ghost':
      return 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800';
    case 'outline':
      return 'border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800';
    default:
      return 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700';
  }
}

// ============================================================================
// Icons
// ============================================================================

const CopyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

// ============================================================================
// CopyButton Component
// ============================================================================

export function CopyButton({
  text,
  variant = 'ghost',
  size = 'md',
  feedback = 'icon',
  label = 'Copy',
  copiedLabel = 'Copied!',
  copiedDuration = 2000,
  onCopy,
  onError,
  showLabel = false,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: CopyButtonProps): React.ReactElement {
  const { copied, copy } = useCopyToClipboard({
    copiedDuration,
    onCopy,
    onError,
  });

  const handleClick = () => {
    if (!disabled) {
      copy(text);
    }
  };

  const isIconOnly = !showLabel;
  const displayLabel = copied ? copiedLabel : label;
  const showIconFeedback = feedback === 'icon' || feedback === 'tooltip';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel ?? displayLabel}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-md font-medium',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        isIconOnly ? iconOnlySizes[size] : sizeStyles[size].button,
        getVariantStyles(variant),
        copied && 'text-green-600 dark:text-green-400',
        className
      )}
    >
      {showIconFeedback ? (
        copied ? (
          <CheckIcon className={sizeStyles[size].icon} />
        ) : (
          <CopyIcon className={sizeStyles[size].icon} />
        )
      ) : (
        <CopyIcon className={sizeStyles[size].icon} />
      )}
      {showLabel && (
        <span>{displayLabel}</span>
      )}
    </button>
  );
}

// ============================================================================
// CopyToClipboard Component (Render Props)
// ============================================================================

export function CopyToClipboard({
  text,
  children,
  copiedDuration = 2000,
  onCopy,
  onError,
}: CopyToClipboardProps): React.ReactElement {
  const { copied, error, copy } = useCopyToClipboard({
    copiedDuration,
    onCopy,
    onError,
  });

  const handleCopy = React.useCallback(() => {
    copy(text);
  }, [copy, text]);

  return <>{children({ copy: handleCopy, copied, error })}</>;
}

// ============================================================================
// ClipboardText Component
// ============================================================================

export function ClipboardText({
  text,
  maxLength,
  showTooltip = true,
  selectable = true,
  hideButtonUntilHover = false,
  size = 'sm',
  className,
}: ClipboardTextProps): React.ReactElement {
  const [isHovered, setIsHovered] = React.useState(false);

  const displayText = maxLength && text.length > maxLength
    ? `${text.slice(0, maxLength)}...`
    : text;

  const isTruncated = maxLength && text.length > maxLength;

  return (
    <div
      className={clsx(
        'inline-flex items-center gap-2 group',
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span
        className={clsx(
          'font-mono text-sm',
          selectable ? 'select-all' : 'select-none',
          'text-gray-700 dark:text-gray-300'
        )}
        title={showTooltip && isTruncated ? text : undefined}
      >
        {displayText}
      </span>
      <CopyButton
        text={text}
        size={size}
        variant="ghost"
        className={clsx(
          hideButtonUntilHover && 'opacity-0 group-hover:opacity-100',
          'transition-opacity'
        )}
        aria-label={`Copy "${displayText}" to clipboard`}
      />
    </div>
  );
}

// ============================================================================
// CodeBlock Component
// ============================================================================

export function CodeBlock({
  code,
  language,
  showLineNumbers = false,
  highlightLines = [],
  showCopyButton = true,
  maxHeight,
  className,
  title,
}: CodeBlockProps): React.ReactElement {
  const lines = code.split('\n');

  return (
    <div
      className={clsx(
        'relative rounded-lg overflow-hidden',
        'bg-gray-900 dark:bg-gray-950',
        className
      )}
    >
      {/* Header with title and copy button */}
      {(title || showCopyButton) && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-800 dark:bg-gray-900 border-b border-gray-700">
          <div className="flex items-center gap-2">
            {title && (
              <span className="text-sm font-medium text-gray-300">{title}</span>
            )}
            {language && (
              <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-400 dark:text-gray-500">
                {language}
              </span>
            )}
          </div>
          {showCopyButton && (
            <CopyButton
              text={code}
              size="sm"
              variant="ghost"
              className="text-gray-400 hover:text-white dark:text-gray-500"
            />
          )}
        </div>
      )}

      {/* Code content */}
      <div
        className={clsx(
          'overflow-auto p-4',
          maxHeight && 'overflow-y-auto'
        )}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <pre className="text-sm font-mono">
          <code>
            {lines.map((line, index) => {
              const lineNumber = index + 1;
              const isHighlighted = highlightLines.includes(lineNumber);

              return (
                <div
                  key={index}
                  className={clsx(
                    'leading-relaxed',
                    isHighlighted && 'bg-blue-500/20 -mx-4 px-4'
                  )}
                >
                  {showLineNumbers && (
                    <span className="inline-block w-8 pr-4 text-right text-gray-500 select-none dark:text-gray-400">
                      {lineNumber}
                    </span>
                  )}
                  <span className="text-gray-100">{line || ' '}</span>
                </div>
              );
            })}
          </code>
        </pre>
      </div>
    </div>
  );
}

// ============================================================================
// CopyInput Component
// ============================================================================

export function CopyInput({
  value,
  label,
  readOnly = true,
  size = 'md',
  showCopyButton = true,
  onCopy,
  className,
}: CopyInputProps): React.ReactElement {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { copied, copy } = useCopyToClipboard({ onCopy });

  const handleCopy = () => {
    copy(value);
    // Select input text on copy
    inputRef.current?.select();
  };

  const inputSizeStyles: Record<string, string> = {
    sm: 'h-8 text-sm px-3',
    md: 'h-10 text-sm px-3',
    lg: 'h-12 text-base px-4',
  };

  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      {label && (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          readOnly={readOnly}
          className={clsx(
            'flex-1 rounded-md border font-mono',
            'border-gray-300 bg-gray-50 text-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700',
            'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            readOnly && 'cursor-default',
            inputSizeStyles[size]
          )}
          onClick={(e) => readOnly && (e.target as HTMLInputElement).select()}
        />
        {showCopyButton && (
          <CopyButton
            text={value}
            size={size}
            variant="outline"
            showLabel
            label="Copy"
            copiedLabel="Copied!"
          />
        )}
      </div>
      {copied && (
        <p className="text-sm text-green-600 dark:text-green-400">
          Copied to clipboard!
        </p>
      )}
    </div>
  );
}

// ============================================================================
// CopyLink Component
// ============================================================================

export function CopyLink({
  url,
  displayText,
  truncate = true,
  maxLength = 40,
  asLink = false,
  onCopy,
  className,
}: CopyLinkProps): React.ReactElement {
  const text = displayText ?? url;
  const displayValue = truncate && text.length > maxLength
    ? `${text.slice(0, maxLength)}...`
    : text;

  const isTruncated = truncate && text.length > maxLength;

  return (
    <div className={clsx('inline-flex items-center gap-2', className)}>
      {asLink ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          title={isTruncated ? text : undefined}
        >
          {displayValue}
        </a>
      ) : (
        <span
          className="text-gray-700 dark:text-gray-300 font-mono text-sm"
          title={isTruncated ? text : undefined}
        >
          {displayValue}
        </span>
      )}
      <CopyButton
        text={url}
        size="sm"
        variant="ghost"
        onCopy={() => onCopy?.(url)}
      />
    </div>
  );
}

// ============================================================================
// ShareButton - Combined copy + share functionality
// ============================================================================

export interface ShareButtonProps {
  /** URL or text to share */
  content: string;
  /** Title for native share dialog */
  title?: string;
  /** Description text for native share */
  description?: string;
  /** Button variant */
  variant?: CopyButtonVariant;
  /** Button size */
  size?: CopyButtonSize;
  /** Prefer native share when available */
  preferNativeShare?: boolean;
  /** Callback when shared/copied */
  onShare?: (method: 'native' | 'clipboard') => void;
  /** Additional class names */
  className?: string;
}

export function ShareButton({
  content,
  title,
  description,
  variant = 'default',
  size = 'md',
  preferNativeShare = true,
  onShare,
  className,
}: ShareButtonProps): React.ReactElement {
  const [shared, setShared] = React.useState(false);
  const { copy, copied } = useCopyToClipboard({ copiedDuration: 2000 });

  const supportsNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const handleShare = async () => {
    if (preferNativeShare && supportsNativeShare) {
      try {
        await navigator.share({
          title,
          text: description,
          url: content,
        });
        setShared(true);
        onShare?.('native');
        setTimeout(() => setShared(false), 2000);
      } catch (err) {
        // User cancelled or share failed, fall back to copy
        if ((err as Error).name !== 'AbortError') {
          await copy(content);
          onShare?.('clipboard');
        }
      }
    } else {
      await copy(content);
      onShare?.('clipboard');
    }
  };

  const isSuccess = shared || copied;

  return (
    <button
      type="button"
      onClick={handleShare}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        sizeStyles[size].button,
        getVariantStyles(variant),
        isSuccess && 'text-green-600 dark:text-green-400',
        className
      )}
    >
      {isSuccess ? (
        <>
          <CheckIcon className={sizeStyles[size].icon} />
          <span>{supportsNativeShare && preferNativeShare ? 'Shared!' : 'Copied!'}</span>
        </>
      ) : (
        <>
          <svg
            className={sizeStyles[size].icon}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
            />
          </svg>
          <span>Share</span>
        </>
      )}
    </button>
  );
}

// ============================================================================
// CopyableText - Inline text with copy on click
// ============================================================================

export interface CopyableTextProps {
  /** Text to display and copy */
  children: string;
  /** Show visual indicator that text is copyable */
  showIndicator?: boolean;
  /** Feedback when copied */
  feedback?: 'tooltip' | 'inline' | 'none';
  /** Callback when copied */
  onCopy?: (text: string) => void;
  /** Additional class names */
  className?: string;
}

export function CopyableText({
  children,
  showIndicator = true,
  feedback = 'tooltip',
  onCopy,
  className,
}: CopyableTextProps): React.ReactElement {
  const { copy, copied } = useCopyToClipboard({ onCopy });

  return (
    <button
      type="button"
      onClick={() => copy(children)}
      className={clsx(
        'inline-flex items-center gap-1 font-mono text-sm',
        'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800',
        'rounded px-1 -mx-1 transition-colors',
        className
      )}
      title={feedback === 'tooltip' ? (copied ? 'Copied!' : 'Click to copy') : undefined}
    >
      <span className={copied ? 'text-green-600 dark:text-green-400' : undefined}>
        {children}
      </span>
      {showIndicator && !copied && (
        <CopyIcon className="w-3 h-3 opacity-50" />
      )}
      {copied && feedback === 'inline' && (
        <CheckIcon className="w-3 h-3 text-green-600 dark:text-green-400" />
      )}
    </button>
  );
}
